import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ConsentScreen.css';
import {
  ArrowForwardRounded,
  InfoOutlined,
  LockOutlined,
  ShieldOutlined,
  TimerOutlined
} from '@mui/icons-material';
import {
  BackButton,
  EnterpriseButton,
  EnterpriseCard,
  EnterpriseGrid,
  EnterpriseHero,
  EnterpriseShell,
  EnterpriseTopBar,
  WarningBanner
} from './ui/EnterpriseLayout';

function ConsentScreen() {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(10);
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);

  useEffect(() => {
    if (!showWarning) return;
    
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, [showWarning]);

  const canContinue = consent1 && consent2;

  return (
    <EnterpriseShell>
      <EnterpriseTopBar
        right={
          <>
            <BackButton onClick={() => navigate(-1)} />
            <span className="vt-chip vt-chip--subtle">ID verification</span>
          </>
        }
      />

      <EnterpriseHero
        title="Consent required"
        subtitle="Before we start, please review and accept the consent items below."
      />

      <EnterpriseGrid columns={1}>
        <EnterpriseCard>
          {showWarning && (
            <div style={{ marginBottom: 14 }}>
              <WarningBanner onClose={() => setShowWarning(false)}>
                For security reasons this session will expire in {timeRemaining} minutes.
              </WarningBanner>
            </div>
          )}

          <div className="vt-cardHeader">
            <div className="vt-iconTile" aria-hidden="true">
              <ShieldOutlined className="vt-iconTileIcon" />
            </div>
            <div>
              <h2 className="vt-cardTitle">What you're agreeing to</h2>
            </div>
          </div>

          <ul className="vt-list">
            <li className="vt-listItem">
              <InfoOutlined className="vt-listIcon" />
              <span>Your face image may be treated as biometric data under some laws.</span>
            </li>
            <li className="vt-listItem">
              <TimerOutlined className="vt-listIcon" />
              <span>Your data is used only to confirm your age and nothing more.</span>
            </li>
            <li className="vt-listItem">
              <LockOutlined className="vt-listIcon" />
              <span>Your data is deleted immediately after the check is complete.</span>
            </li>
          </ul>

          <div style={{ height: 14 }} />

          <div className="vt-card" style={{ padding: 14, boxShadow: 'var(--vt-shadow-soft)' }}>
            <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={consent1}
                onChange={(e) => setConsent1(e.target.checked)}
                style={{ width: 18, height: 18, marginTop: 2 }}
              />
              <span style={{ color: 'var(--vt-muted)', lineHeight: 1.5 }}>
                I consent to Veritage&apos;s collection and use of my data to confirm my age.
              </span>
            </label>
            <div style={{ height: 10 }} />
            <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={consent2}
                onChange={(e) => setConsent2(e.target.checked)}
                style={{ width: 18, height: 18, marginTop: 2 }}
              />
              <span style={{ color: 'var(--vt-muted)', lineHeight: 1.5 }}>
                I have read and acknowledge Veritage&apos;s privacy notice including biometrics policy.
              </span>
            </label>
          </div>

          <div className="vt-actions">
            <EnterpriseButton variant="primary" onClick={() => navigate('/face-capture')} disabled={!canContinue}>
              <span>Continue</span>
              <ArrowForwardRounded className="vt-btnIcon" />
            </EnterpriseButton>
            <EnterpriseButton variant="secondary" type="button" onClick={() => navigate('/')}>
              Back to options
            </EnterpriseButton>
          </div>
        </EnterpriseCard>
      </EnterpriseGrid>
    </EnterpriseShell>
  );
}

export default ConsentScreen;
